import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { selectCanManageSectionsTables } from '@store/auth/authSelectors';
import { selectEffectiveOutlet, selectEffectiveOutletId } from '@store/outlets';
import {
  createOutletPrinterThunk,
  fetchInstalledPrintersThunk,
  fetchOutletPrintersThunk,
  selectInstalledPrinters,
  selectOutletPrinters,
  selectPrintersError,
  selectPrintersLoading,
  selectPrintersSaving,
  selectPrintersTesting,
  testPrinterConnectionThunk,
} from '@store/printers';
import { Printer, RefreshCcw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { CreateOutletPrinterDraft } from '../../../types/printer';
import { apiError, apiSuccess } from '../../../util/notify';
import { ONBOARDING_TOUR_TARGETS } from '../../../services/onboardingTourService';
import { Button, EmptyState, LoadingState } from '../../common';
import { AddPrinterModal } from './AddPrinterModal';

export const PrintersTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const outletId = useAppSelector(selectEffectiveOutletId);
  const selectedOutlet = useAppSelector(selectEffectiveOutlet);
  const printers = useAppSelector(selectOutletPrinters);
  const installedPrinters = useAppSelector(selectInstalledPrinters);
  const isLoading = useAppSelector(selectPrintersLoading);
  const isSaving = useAppSelector(selectPrintersSaving);
  const isTesting = useAppSelector(selectPrintersTesting);
  const error = useAppSelector(selectPrintersError);
  const canManageSectionsTables = useAppSelector(selectCanManageSectionsTables);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  useEffect(() => {
    if (!outletId) return;

    dispatch(fetchOutletPrintersThunk(outletId));
  }, [dispatch, outletId]);

  const handleOpenAddModal = async () => {
    if (!canManageSectionsTables) return;
    await dispatch(fetchInstalledPrintersThunk());
    setIsAddModalOpen(true);
  };

  const handleCreatePrinter = async (draft: CreateOutletPrinterDraft) => {
    if (!outletId || !canManageSectionsTables) {
      apiError('Select an outlet first');
      return false;
    }

    const result = await dispatch(
      createOutletPrinterThunk({
        ...draft,
        outlet_id: outletId,
      }),
    );

    if (createOutletPrinterThunk.rejected.match(result)) {
      apiError(result.payload || 'Failed to create printer');
      return false;
    }

    await dispatch(fetchOutletPrintersThunk(outletId));
    apiSuccess('Printer added successfully');
    setIsAddModalOpen(false);
    return true;
  };

  const handleTestPrinter = async (draft: CreateOutletPrinterDraft) => {
    const result = await dispatch(testPrinterConnectionThunk(draft));
    if (testPrinterConnectionThunk.rejected.match(result)) {
      apiError(result.payload || 'Printer test failed');
      return false;
    }

    apiSuccess('Test page sent successfully');
    return true;
  };

  const handleRefresh = () => {
    if (!outletId) return;

    dispatch(fetchOutletPrintersThunk(outletId));
    dispatch(fetchInstalledPrintersThunk());
  };

  if (isLoading && printers.length === 0) {
    return <LoadingState message="Loading printers..." />;
  }

  return (
    <div
      className="space-y-4"
      data-onboarding-tour={ONBOARDING_TOUR_TARGETS.printersPage}
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold uppercase tracking-wide text-black">
            Printers
          </h2>
          <p className="text-xs text-gray-600">
            Configure USB, Serial, Bluetooth, and Network printers for bill and
            department KOT routing per section.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            className="text-xs"
            disabled={!outletId}
          >
            <RefreshCcw size={14} />
            Refresh
          </Button>
          {canManageSectionsTables ? (
            <Button
              onClick={handleOpenAddModal}
              disabled={!outletId}
              data-onboarding-tour={ONBOARDING_TOUR_TARGETS.printersAddButton}
            >
              <Printer size={16} />
              Add Printer
            </Button>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
          {error}
        </div>
      ) : null}

      {printers.length === 0 ? (
        <EmptyState
          title="No printers added"
          description={
            canManageSectionsTables
              ? 'Add installed or network printers to configure section bill printing and department KOT routing.'
              : 'Printers configured for this outlet will appear here once an authorized user adds them.'
          }
          icon="package"
          primaryAction={
            canManageSectionsTables
              ? {
                  dataTourId: ONBOARDING_TOUR_TARGETS.printersAddButton,
                  label: 'Add Printer',
                  onClick: handleOpenAddModal,
                  icon: <Printer size={16} />,
                }
              : undefined
          }
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="w-full">
            <thead>
              <tr className="app-bg border-b border-gray-200">
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-black">
                  Outlet
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-black">
                  Printer Name / Address
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-black">
                  Details
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-black">
                  Connection Type
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-black">
                  Paper Size
                </th>
              </tr>
            </thead>
            <tbody>
              {printers.map((printer) => (
                <tr key={printer.id} className="border-b border-gray-100">
                  <td className="px-3 py-3 text-sm text-gray-700">
                    {printer.outlet_name || selectedOutlet?.name || '—'}
                  </td>
                  <td className="px-3 py-3 text-sm text-black">
                    <div className="font-semibold uppercase">
                      {printer.name}
                    </div>
                    <div className="text-xs text-gray-600">
                      {printer.connection_type === 'network'
                        ? printer.ip_address || printer.printer_identifier
                        : printer.printer_identifier}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-700">
                    {printer.connection_type === 'network'
                      ? `Host ${printer.ip_address || printer.printer_identifier} • Port ${
                          printer.port || 9100
                        }`
                      : printer.connection_type === 'serial'
                        ? `Path ${printer.printer_identifier}`
                        : printer.connection_type === 'bluetooth'
                          ? `Address ${printer.printer_identifier}`
                          : `Installed device ${printer.printer_identifier}`}
                  </td>
                  <td className="px-3 py-3 text-sm uppercase text-black">
                    {printer.connection_type}
                  </td>
                  <td className="px-3 py-3 text-sm uppercase text-black">
                    {printer.paper_size}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddPrinterModal
        installedPrinters={installedPrinters}
        isOpen={isAddModalOpen}
        isSaving={isSaving}
        isTesting={isTesting}
        onClose={() => setIsAddModalOpen(false)}
        onTest={handleTestPrinter}
        onSubmit={handleCreatePrinter}
      />
    </div>
  );
};
